import { useState } from 'react'
import { AlertTriangle, BarChart2, Flame, Search, ShieldAlert, Signal as SignalIcon } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import AlertLogTab from '../components/competitive/AlertLogTab'
import CompetitorTab from '../components/competitive/CompetitorTab'
import DashboardTab from '../components/competitive/DashboardTab'
import EmergingBrandsTab from '../components/competitive/EmergingBrandsTab'
import KeywordIntelTab from '../components/competitive/KeywordIntelTab'
import SignalFeedTab from '../components/competitive/SignalFeedTab'
import type { RadarTab } from '../types/competitive'

const tabItems: Array<{ key: RadarTab; label: string; subtitle: string; icon: LucideIcon }> = [
  {
    key: 'dashboard',
    label: 'Dashboard',
    subtitle: "Your daily intelligence brief — what's happening and what to do",
    icon: BarChart2,
  },
  {
    key: 'competitors',
    label: 'Competitors',
    subtitle: 'All tracked competitors ranked by threat level',
    icon: ShieldAlert,
  },
  {
    key: 'signals',
    label: 'Signal Feed',
    subtitle: 'Threat stories clustered from raw signals',
    icon: SignalIcon,
  },
  {
    key: 'emerging',
    label: 'Emerging Brands',
    subtitle: 'New brands appearing in your product categories',
    icon: Flame,
  },
  {
    key: 'keywords',
    label: 'Keywords',
    subtitle: "Branded search defense — who's intruding on your terms",
    icon: Search,
  },
  {
    key: 'alerts',
    label: 'Alert Log',
    subtitle: 'History of all triggered alerts',
    icon: AlertTriangle,
  },
]

export default function CompetitiveRadar() {
  const [activeTab, setActiveTab] = useState<RadarTab>('dashboard')
  const [competitorFilter, setCompetitorFilter] = useState('')
  const activeTabConfig = tabItems.find((tab) => tab.key === activeTab) ?? tabItems[0]

  const handleNavigateToCompetitor = (competitorName: string) => {
    setCompetitorFilter(competitorName)
    setActiveTab('competitors')
  }

  return (
    <div className="min-h-screen bg-slate-50 p-6">
      <div className="mx-auto max-w-7xl space-y-6">
        <div className="rounded-lg border border-slate-200 bg-white shadow-sm">
          <div className="overflow-x-auto border-b border-slate-200 p-2">
            <div className="flex min-w-max flex-nowrap items-center gap-1">
              {tabItems.map(({ key, label, icon: Icon }) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => setActiveTab(key)}
                  className={`inline-flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                    activeTab === key
                      ? 'bg-[var(--patterniq-navy-900)] text-white shadow-sm'
                      : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
                  } focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1`}
                >
                  <Icon className="h-4 w-4" />
                  {label}
                </button>
              ))}
            </div>
          </div>

          <div className="p-5">
            <div className="mb-4">
              <h2 className="text-lg font-semibold text-slate-900">{activeTabConfig.label}</h2>
              <p className="text-sm text-slate-500">{activeTabConfig.subtitle}</p>
            </div>
            {activeTab === 'dashboard' && <DashboardTab onDeepLinkCompetitor={(_, name) => handleNavigateToCompetitor(name)} />}
            {activeTab === 'competitors' && (
              <CompetitorTab
                competitorFilter={competitorFilter}
                setCompetitorFilter={setCompetitorFilter}
              />
            )}
            {activeTab === 'signals' && (
              <SignalFeedTab onNavigateToCompetitor={handleNavigateToCompetitor} />
            )}
            {activeTab === 'emerging' && <EmergingBrandsTab />}
            {activeTab === 'keywords' && <KeywordIntelTab />}
            {activeTab === 'alerts' && <AlertLogTab />}
          </div>
        </div>
      </div>
    </div>
  )
}
